// Mergt fehlende Vorlagen-Sections (z.B. Anlage 2) aus dem aktuellen Original-Template in
// einen ggf. älteren gespeicherten Stand des Nutzers, ohne dessen sonstige Edits zu verlieren.
//
// Stabile Section-Identifikation (Priorität in dieser Reihenfolge):
//   1. data-section-key="..."   — explizit gepflegt, zukunftssicher
//   2. id="..."                  — element-id
//   3. eindeutige CSS-Klasse wie "anlage2-page", "signature-page" — Konvention

const ANLAGE2_CSS_START = '/* anlage2-css-start */';
const ANLAGE2_CSS_END = '/* anlage2-css-end */';
const DACHPACHT_CONTRACT_KEYS = new Set([
  'pachtvertrag-dachverpachtung-ez',
  'pachtvertrag-dachverpachtung-jz',
]);

export function normalizeDachverpachtungPartyLabels(html, contractKey) {
  if (typeof html !== 'string' || !html) return html;
  if (!DACHPACHT_CONTRACT_KEYS.has(String(contractKey || '').toLowerCase())) return html;

  return html
    .replace(/Firma(?!:)(\s+)(\{\{\s*Firma\s*\}\})/g, 'Firma:$1$2')
    .replace(/Vorname\/Name(?!:)(\s+)(\{\{\s*Name Vertragspartner\s*\}\})/g, 'Vorname/Name:$1$2')
    .replace(/(Stra(?:\u00df|&#223;)e)(?!:)(\s+)(\{\{\s*Stra(?:\u00df|&#223;)e, Hausnummer\s*\}\})/g, '$1:$2$3')
    .replace(/PLZ\/\s*Ort(?!:)(\s+)(\{\{\s*Postleitzahl, Ort\s*\}\})/g, 'PLZ/Ort:$1$2');
}

function getSectionKey(node) {
  if (!node || node.nodeType !== 1) return null;
  const explicit = node.getAttribute && node.getAttribute('data-section-key');
  if (explicit) return String(explicit).trim();
  if (node.id) return `id:${node.id}`;
  const classNameAttr = node.getAttribute ? (node.getAttribute('class') || '') : '';
  const classes = String(classNameAttr || '').split(/\s+/).filter(Boolean);
  const sectionClass = classes.find((c) => /-(page|section|annex)$/i.test(c) || /^(anlage|annex)\d+/i.test(c));
  return sectionClass ? `class:${sectionClass}` : null;
}

function getTopLevelSections(doc) {
  if (!doc) return [];
  const source = doc.getElementById('contract-source');
  if (!source) return [];
  const sections = [];
  const children = Array.from(source.children || []);
  for (const child of children) {
    const key = getSectionKey(child);
    if (key) sections.push({ key, node: child });
  }
  return sections;
}

function extractAnlage2Css(originalHtml) {
  if (typeof originalHtml !== 'string' || !originalHtml) return '';
  const startIdx = originalHtml.indexOf(ANLAGE2_CSS_START);
  const endIdx = originalHtml.indexOf(ANLAGE2_CSS_END);
  if (startIdx < 0 || endIdx < 0 || endIdx <= startIdx) return '';
  return originalHtml.slice(startIdx, endIdx + ANLAGE2_CSS_END.length);
}

function findAnlage2Section(doc) {
  if (!doc) return null;
  const source = doc.getElementById('contract-source');
  if (!source) return null;
  return source.querySelector('section.anlage2-page');
}

function findAnlage5Heading(doc) {
  if (!doc) return null;
  const source = doc.getElementById('contract-source');
  if (!source) return null;
  const headings = source.querySelectorAll('h2.annex-heading');
  for (const heading of headings) {
    const span = heading.querySelector('span');
    if (span && span.textContent && /Anlage\s*5/i.test(span.textContent)) {
      return heading;
    }
  }
  return null;
}

function hasKaufKleinspeicherAnnexLayout(html) {
  const source = String(html || '');
  return source.includes('Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI.pdf') &&
    source.includes('Technische Beschreibung/Datenblatt Alpha ESS Storion LC TB 125') &&
    source.includes('kauf-annex-7-heading');
}

function syncKaufKleinspeicherReferences(html, originalHtml) {
  if (!hasKaufKleinspeicherAnnexLayout(originalHtml)) return html;
  return String(html || '')
    .replace(/als Anlage 7 beigefügten Pachtvertrages Kleinspeicher/g, 'als Anlage 8 beigefügten Pachtvertrages Kleinspeicher')
    .replace(/den als Anlage 7 beigefügten Pachtvertrag Kleinspeicher/g, 'den als Anlage 8 beigefügten Pachtvertrag Kleinspeicher')
    .replace(/als Anlage 7 beigefügt/g, 'als Anlage 8 beigefügt');
}

function syncKaufKleinspeicherAnnexOverview(savedDoc, originalDoc, originalHtml) {
  if (!savedDoc || !originalDoc || !hasKaufKleinspeicherAnnexLayout(originalHtml)) return false;
  const savedSource = savedDoc.getElementById('contract-source');
  const originalSource = originalDoc.getElementById('contract-source');
  if (!savedSource || !originalSource) return false;

  const savedOverview = savedSource.querySelector('.annex-overview');
  const originalOverview = originalSource.querySelector('.annex-overview');
  if (!savedOverview || !originalOverview) return false;

  if ((savedOverview.outerHTML || '') === (originalOverview.outerHTML || '')) return false;

  savedOverview.replaceWith(savedDoc.importNode(originalOverview, true));
  return true;
}

function findAnnexHeadingByNumber(doc, number) {
  const source = doc?.getElementById?.('contract-source');
  if (!source) return null;
  const headings = Array.from(source.querySelectorAll('h2.annex-heading'));
  return headings.find((heading) => {
    const label = heading.querySelector('span')?.textContent || '';
    return new RegExp(`Anlage\\s*${number}\\b`, 'i').test(label);
  }) || null;
}

function findNextDirectAnnexAfter(node, minNumber) {
  let current = node?.nextElementSibling || null;
  while (current) {
    if (current.matches?.('h2.annex-heading')) {
      const label = current.querySelector('span')?.textContent || '';
      const match = label.match(/Anlage\s*(\d+)/i);
      if (match && Number(match[1]) >= minNumber) return current;
    }
    current = current.nextElementSibling;
  }
  return null;
}

function collectDirectKleinspeicherAnnexNodes(doc) {
  const source = doc?.getElementById?.('contract-source');
  if (!source) return [];
  const start = source.querySelector('section.anlage2-page');
  if (!start) return [];
  const end = findNextDirectAnnexAfter(findAnnexHeadingByNumber(doc, 7) || start, 8);
  const nodes = [];
  let current = start;
  while (current && current !== end) {
    nodes.push(current);
    current = current.nextElementSibling;
  }
  return nodes;
}

function hasCompleteKleinspeicherVollmachtDetails(nodes) {
  const signature = (nodes || []).map((node) => node.textContent || node.outerHTML || '').join('\n');
  return signature.includes('Firma:') &&
    signature.includes('{{Name Vertragspartner}}') &&
    signature.includes('{{Postleitzahl, Ort, ET}}') &&
    signature.includes('Anschrift des Pachtobjektes');
}

function wouldDowngradeKleinspeicherVollmachtDetails(savedDoc, originalDoc) {
  const savedNodes = collectDirectKleinspeicherAnnexNodes(savedDoc);
  const originalNodes = collectDirectKleinspeicherAnnexNodes(originalDoc);
  return hasCompleteKleinspeicherVollmachtDetails(savedNodes) &&
    !hasCompleteKleinspeicherVollmachtDetails(originalNodes);
}

function syncKleinspeicherSharedAnnexes(savedDoc, originalDoc) {
  const savedSource = savedDoc?.getElementById?.('contract-source');
  const originalNodes = collectDirectKleinspeicherAnnexNodes(originalDoc);
  const savedNodes = collectDirectKleinspeicherAnnexNodes(savedDoc);
  if (!savedSource || originalNodes.length === 0 || savedNodes.length === 0) return false;
  if (wouldDowngradeKleinspeicherVollmachtDetails(savedDoc, originalDoc)) return false;

  const originalSignature = originalNodes.map((node) => node.outerHTML || '').join('\n');
  const savedSignature = savedNodes.map((node) => node.outerHTML || '').join('\n');
  if (originalSignature === savedSignature) return false;

  const anchor = savedNodes[0];
  originalNodes.forEach((node) => {
    savedSource.insertBefore(savedDoc.importNode(node, true), anchor);
  });
  savedNodes.forEach((node) => node.remove());
  return true;
}

function getSourceChildrenBySectionPrefix(doc, prefix) {
  const source = doc?.getElementById?.('contract-source');
  if (!source || !prefix) return [];
  return Array.from(source.children || []).filter((child) => {
    const key = child.getAttribute?.('data-section-key') || child.getAttribute?.('data-editor-id') || '';
    return key === prefix || String(key).startsWith(`${prefix}-`);
  });
}

function getSourceSectionKeys(doc, prefix) {
  return getSourceChildrenBySectionPrefix(doc, prefix).map((child) => (
    child.getAttribute?.('data-section-key') || child.getAttribute?.('data-editor-id') || ''
  ));
}

function getSourceSectionSignatures(doc, prefix) {
  return getSourceChildrenBySectionPrefix(doc, prefix).map((child) => (
    child.outerHTML || ''
  ));
}

function sectionListsEqual(left, right) {
  if (left.length !== right.length) return false;
  return left.every((key, index) => key === right[index]);
}

function removeKaufKleinspeicherSectionGroup(savedDoc, prefix) {
  const nodes = getSourceChildrenBySectionPrefix(savedDoc, prefix);
  if (nodes.length === 0) return false;
  nodes.forEach((node) => node.remove());
  return true;
}

function replaceKaufKleinspeicherSectionGroup(savedDoc, originalDoc, prefix, nextPrefix) {
  const savedSource = savedDoc?.getElementById?.('contract-source');
  if (!savedSource) return false;

  const originalNodes = getSourceChildrenBySectionPrefix(originalDoc, prefix);
  if (originalNodes.length === 0) return false;

  const savedKeys = getSourceSectionKeys(savedDoc, prefix);
  const originalKeys = getSourceSectionKeys(originalDoc, prefix);
  const savedSignatures = getSourceSectionSignatures(savedDoc, prefix);
  const originalSignatures = getSourceSectionSignatures(originalDoc, prefix);
  if (sectionListsEqual(savedKeys, originalKeys) && sectionListsEqual(savedSignatures, originalSignatures)) return false;

  const existingNodes = getSourceChildrenBySectionPrefix(savedDoc, prefix);
  const nextAnchor = nextPrefix ? getSourceChildrenBySectionPrefix(savedDoc, nextPrefix)[0] : null;
  const anchor = existingNodes[0] || nextAnchor || null;

  originalNodes.forEach((node) => {
    const imported = savedDoc.importNode(node, true);
    if (anchor && anchor.parentNode === savedSource) {
      savedSource.insertBefore(imported, anchor);
    } else {
      savedSource.appendChild(imported);
    }
  });
  existingNodes.forEach((node) => node.remove());
  return true;
}

function syncKaufKleinspeicherDirectAnnexes(savedDoc, originalDoc, originalHtml) {
  if (!savedDoc || !originalDoc || !hasKaufKleinspeicherAnnexLayout(originalHtml)) return false;
  if (wouldDowngradeKleinspeicherVollmachtDetails(savedDoc, originalDoc)) return false;
  let changed = false;
  changed = removeKaufKleinspeicherSectionGroup(savedDoc, 'kauf-annex-3') || changed;
  changed = replaceKaufKleinspeicherSectionGroup(savedDoc, originalDoc, 'kauf-annex-5', 'kauf-annex-6') || changed;
  changed = replaceKaufKleinspeicherSectionGroup(savedDoc, originalDoc, 'kauf-annex-7', null) || changed;
  return changed;
}

function upsertAnlage2Css(savedDoc, css) {
  if (!savedDoc || !css) return;

  const styleTags = Array.from(savedDoc.querySelectorAll('style'));
  for (const styleTag of styleTags) {
    const text = styleTag.textContent || '';
    const startIdx = text.indexOf(ANLAGE2_CSS_START);
    const endIdx = text.indexOf(ANLAGE2_CSS_END);
    if (startIdx >= 0 && endIdx > startIdx) {
      const before = text.slice(0, startIdx).replace(/\s*$/, '');
      const after = text.slice(endIdx + ANLAGE2_CSS_END.length).replace(/^\s*/, '');
      styleTag.textContent = `${before}\n    ${css}\n${after}`;
      return;
    }
  }

  const firstStyle = savedDoc.querySelector('style');
  if (firstStyle) {
    firstStyle.textContent = `${firstStyle.textContent}\n    ${css}\n`;
    return;
  }

  const newStyle = savedDoc.createElement('style');
  newStyle.textContent = css;
  if (savedDoc.head) savedDoc.head.appendChild(newStyle);
}

function detectAnlage2ObjectFit(css) {
  if (!css) return null;
  const match = css.match(/\.anlage2-page-mapbox\s+img\s*\{[\s\S]*?object-fit:\s*(contain|cover)\s*;/i);
  return match ? match[1].toLowerCase() : null;
}

function normalizeLegacyAnlage2ObjectFit(savedDoc, targetObjectFit) {
  if (!savedDoc || !targetObjectFit) return;
  const styleTags = Array.from(savedDoc.querySelectorAll('style'));
  for (const styleTag of styleTags) {
    const text = styleTag.textContent || '';
    if (!text.includes('.anlage2-page-mapbox img')) continue;
    let normalized = text.replace(
      /(\.anlage2-page-mapbox\s+img\s*\{[\s\S]*?object-fit:\s*)(cover|contain)(\s*;)/gi,
      `$1${targetObjectFit}$3`,
    );

    normalized = normalized.replace(
      /(\.anlage2-page-mapbox\s*\{)([\s\S]*?)(\})/gi,
      (full, open, body, close) => {
        if (/box-sizing\s*:/i.test(body)) return full;
        return `${open}${body}\n      box-sizing: border-box;\n    ${close}`;
      },
    );

    styleTag.textContent = normalized;
  }
}

// Generischer Merge: fügt alle Top-Level-Sections aus Original ein, die im Saved fehlen.
// Position wird relativ zu existierenden Sections im Saved bestimmt.
export function mergeMissingSections(savedHtml, originalHtml) {
  if (typeof savedHtml !== 'string' || !savedHtml) return { html: savedHtml, mergedKeys: [] };
  if (typeof originalHtml !== 'string' || !originalHtml) return { html: savedHtml, mergedKeys: [] };

  try {
    const parser = new DOMParser();
    const savedDoc = parser.parseFromString(savedHtml, 'text/html');
    const originalDoc = parser.parseFromString(originalHtml, 'text/html');

    const savedSource = savedDoc.getElementById('contract-source');
    if (!savedSource) return { html: savedHtml, mergedKeys: [] };

    const originalSections = getTopLevelSections(originalDoc);
    if (originalSections.length === 0) return { html: savedHtml, mergedKeys: [] };

    const savedSectionMap = new Map();
    getTopLevelSections(savedDoc).forEach((entry) => savedSectionMap.set(entry.key, entry.node));

    const mergedKeys = [];

    for (let i = 0; i < originalSections.length; i++) {
      const orig = originalSections[i];
      if (savedSectionMap.has(orig.key)) continue;

      // Anker bestimmen: nächste Section im Original, die auch im Saved existiert → davor einfügen
      let anchorNode = null;
      for (let j = i + 1; j < originalSections.length; j++) {
        const candidate = savedSectionMap.get(originalSections[j].key);
        if (candidate) { anchorNode = candidate; break; }
      }

      const imported = savedDoc.importNode(orig.node, true);
      if (anchorNode && anchorNode.parentNode === savedSource) {
        savedSource.insertBefore(imported, anchorNode);
      } else {
        savedSource.appendChild(imported);
      }
      savedSectionMap.set(orig.key, imported);
      mergedKeys.push(orig.key);
    }

    if (mergedKeys.length === 0) return { html: savedHtml, mergedKeys: [] };

    return {
      html: '<!doctype html>' + savedDoc.documentElement.outerHTML,
      mergedKeys,
    };
  } catch (error) {
    console.error('mergeMissingSections failed:', error);
    return { html: savedHtml, mergedKeys: [] };
  }
}

export function mergeMissingAnlage2(savedHtml, originalHtml) {
  if (typeof savedHtml !== 'string' || !savedHtml) return savedHtml;
  if (typeof originalHtml !== 'string' || !originalHtml) return savedHtml;

  // Erst generischer Merge für ALLE fehlenden Sections
  const generic = mergeMissingSections(savedHtml, originalHtml);
  let result = syncKaufKleinspeicherReferences(generic.html, originalHtml);

  // Dann die Anlage-2-spezifische CSS-Behandlung (legacy, bleibt für Bestandsstände wichtig)
  if (!originalHtml.includes('anlage2-page')) return result;

  try {
    const parser = new DOMParser();
    const savedDoc = parser.parseFromString(result, 'text/html');
    const originalDoc = parser.parseFromString(originalHtml, 'text/html');

    syncKaufKleinspeicherAnnexOverview(savedDoc, originalDoc, originalHtml);
    syncKaufKleinspeicherDirectAnnexes(savedDoc, originalDoc, originalHtml);
    // Nur fuer die Kleinspeicher-Familie: der Abgleich ersetzt den kompletten
    // Anlagenbereich (Lageplan bis Anlage 8) durch die Vorlagenfassung. Bei
    // anderen Vertraegen (Dachverpachtung, Grossspeicher, Sanierung) loeschte
    // er dadurch gespeicherte Anlagen aus der Vorschau - und beim naechsten
    // Speichern auch aus dem Stand.
    const istKleinspeicherVorlage = /Kleinspeicher\s*(?:·|&#183;|&middot;)/.test(originalHtml)
      || hasKaufKleinspeicherAnnexLayout(originalHtml);
    if (istKleinspeicherVorlage) {
      syncKleinspeicherSharedAnnexes(savedDoc, originalDoc);
    }

    const originalAnlage2 = findAnlage2Section(originalDoc);
    if (!originalAnlage2) return result;

    const savedSource = savedDoc.getElementById('contract-source');
    if (!savedSource) return result;

    // Sicherheitsnetz: falls Anlage 2 trotzdem fehlt, mit Anlage-5-Anker einfügen
    if (!findAnlage2Section(savedDoc)) {
      const importedSection = savedDoc.importNode(originalAnlage2, true);
      const savedAnlage5 = findAnlage5Heading(savedDoc);
      if (savedAnlage5) {
        savedSource.insertBefore(importedSection, savedAnlage5);
      } else {
        savedSource.appendChild(importedSection);
      }
    }

    const css = extractAnlage2Css(originalHtml);
    if (css) {
      upsertAnlage2Css(savedDoc, css);
      const targetObjectFit = detectAnlage2ObjectFit(css);
      normalizeLegacyAnlage2ObjectFit(savedDoc, targetObjectFit);
    }

    return '<!doctype html>' + savedDoc.documentElement.outerHTML;
  } catch (error) {
    console.error('mergeMissingAnlage2 failed:', error);
    return result;
  }
}
